// =============================================================================
// Project Management module — workspace (the module's front page, at /project-management)
//
// A multi-view workspace (monday.com/ClickUp-style): one filtered dataset rendered as
// any of several views via a view switcher. The chosen view is persisted per browser.
//
// ---------------------------------------------------------------------------
// TO REMOVE THIS PROTOTYPE ENTIRELY:
//   1. Delete: imports/api/projectManagement.ts, imports/api/projectManagementLifecycles.ts,
//      server/methods/projectManagementMethods.ts, server/projectManagementAccess.ts,
//      server/publications/projectManagementPublications.ts, imports/ui/ProjectManagement*.tsx
//   2. Remove the `import '/server/methods/projectManagementMethods';` line in server/methods/index.ts
//      and the publications import in server/publications/index.ts
//   3. Remove the Project Management nav items in imports/ui/Nav.tsx (Inventory Management is separate)
//   4. Remove the PM imports + the "Project Management module" <Route> block in App.tsx
//   5. (Optional) remove the PM strings + the projectManagement* Publications keys.
//   Mongo collections projectManagement* can be dropped manually if desired.
// ---------------------------------------------------------------------------
// =============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Button } from '/imports/ui/Button';
import { Select } from '/imports/ui/Select';
import { Checkbox } from '/imports/ui/Checkbox';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { DividerTabs } from '/imports/ui/DividerTabs';
import { ProjectManagementMetricsBanner, projectManagementMetricDot } from '/imports/ui/ProjectManagementMetricsBanner';
import { projectStatusColor } from '/imports/ui/ProjectManagementProjectStatus';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { FormField } from '/imports/ui/FormField';
import { AddressInput } from '/imports/ui/AddressInput';
import { ImageInput } from '/imports/ui/ImageInput';
import { PartyPicker } from '/imports/ui/ProjectManagementPartyPicker';
import { Input } from '/imports/ui/Input';
import { NumberInput } from '/imports/ui/NumberInput';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { toastOnError } from '/imports/ui/utils';
import { useUser, useFind, useFindOne, useSubscribe, useTracker, useMethod } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Accounts } from '/imports/api/accounts';
import { autoForm, FormSpec } from '/imports/ui/AutoForm';
import { ProjectManagementTableView } from '/imports/ui/ProjectManagementTableView';
import { ProjectManagementGanttView } from '/imports/ui/ProjectManagementGanttView';
import { ProjectManagementScheduleView } from '/imports/ui/ProjectManagementScheduleView';
import { ProjectManagementFinancesView } from '/imports/ui/ProjectManagementFinancesView';
import { ProjectManagementMapView } from '/imports/ui/ProjectManagementMapView';
import {
  ProjectManagementProject, ProjectManagementMilestone, ProjectManagementCashEvent,
  ProjectManagementPipelineRow, ProjectManagementProjects, ProjectManagementMilestones, ProjectManagementCashEvents, ProjectManagementFundingSources, ProjectManagementFundingSource, ProjectManagementPortfolios, ProjectManagementTasks, ProjectManagementTask,
  ProjectManagementOwners, ProjectManagementEpcs, ProjectManagementUtilities,
  projectManagementOwnerModelClass, projectManagementEpcModelClass, projectManagementUtilityModelClass,
  ProjectManagementParameters, ProjectManagementParameter,
  projectManagementSavePortfolio,
  projectManagementFinancialSummary, projectManagementUsesLedger, projectManagementFundingSourcesEstimate, projectManagementTotalFees, projectManagementCompleteProjectFees, projectManagementUseFees, projectManagementCreateProject, projectManagementCanCreateProject, projectManagementLifecyclesInUse, projectManagementCanAccess, projectManagementIsAdmin, projectManagementCanSeeFees, projectManagementDataRoomExport,
  projectManagementReviewSummary,
  projectManagementBatchUploadDocument, projectManagementPipelineSummary,
  projectManagementExternalIdLabel, projectManagementMoney as money,
  projectManagementProjectStatus, projectManagementProjectStatuses, ProjectManagementProjectStatus, projectManagementStatusOrder, projectStatusLabel, projectManagementStateFromAddress,
} from '/imports/api/projectManagement';
import { uploadFile, fileHref } from '/imports/api/files';
import { formatBytes, formatNumber } from '/imports/utils';
import { projectManagementLifecycles, getProjectManagementLifecycle, projectManagementDefaultLifecycleKey, projectManagementFeeUseCodes, projectManagementFinanceModel, projectManagementSourcesFigure, projectManagementWorkspaceLifecycle, projectManagementSetWorkspaceLifecycle, ProjectManagementLifecycle } from '/imports/api/projectManagementLifecycles';

const ProjectManagementPipelinePageStyled = styled(Page)`
  .page-body { height: 0; display: flex; flex-direction: column; }
  .heading-buttons { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
  .controls { display: flex; gap: 1rem; align-items: center; margin-bottom: 0.75rem; flex-wrap: wrap; }
  .controls .lifecycle-select { min-width: 18rem; }
  .controls .status-select { min-width: 9rem; }
  .controls .portfolio-select { min-width: 14rem; }
  .controls .new-portfolio { margin-left: -0.75rem; }
  .controls .search { min-width: 16rem; max-width: 28rem; flex: 1; }
  .view-switch { display: flex; gap: 0.4rem; margin-bottom: 1rem; flex-wrap: wrap; }
  /* Placement only — the banner's own styling lives in ProjectManagementMetricsBanner. */
  .metrics-banner { margin: 0 0 0.75rem; }
  /* The Projects tile puts its count on the label row and gives the value area to the four installed
     sizes — two to a row in a fixed grid, so the break always falls after the second status rather than
     wherever the labels happen to run out of room, and the tile stays as wide as every other one. The
     tile's own figures carry their sizes, so it opts out of the banner's single-figure type scale. */
  .metrics-banner .metric.projects .metric-value { font-size: inherit; line-height: inherit; }
  .metrics-banner .status-split { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem 0.4rem; }
  .metrics-banner .status-count {
    display: flex; flex-direction: column; align-items: flex-start; gap: 0.05rem; min-width: 0;
    padding: 0; border: none; background: none; font: inherit; color: inherit; cursor: pointer; text-align: left;
  }
  /* Scales with the tile it landed in, as the banner's own figures do: a wide tile has the room for a
     bigger figure, a narrow one does not, and a size that cannot break (21.08MW has no break opportunity)
     must never be set larger than its column. */
  .metrics-banner .status-count .n {
    display: inline-flex; align-items: center; gap: 0.3rem; max-width: 100%;
    font-size: clamp(0.8125rem, 6.5cqw, 1.125rem); font-weight: 600; line-height: 1.2; color: rgba(255, 255, 255, 0.72);
  }
  /* .metric-value carries overflow-wrap: break-word for a long single figure, which here would split a
     size mid-token ("21.08M / W"). Normal wrapping leaves the zero-width space after the slash as the only
     break, so a size either sits on one line or breaks between its power and storage halves — never
     inside a number. */
  .metrics-banner .status-count .n .v { min-width: 0; overflow-wrap: normal; }
  /* The project count rides after the size, inside .v so the space between them is a break opportunity: a
     narrow tile (a super user's, with three money tiles beside it) drops "(12)" to its own line rather than
     spilling. Lighter and smaller than the size, which is the figure being compared across the four bins. */
  .metrics-banner .status-count .n .v .c { font-size: 0.95em; font-weight: 600; color: rgba(255, 255, 255, 0.62); }
  .metrics-banner .status-count:hover .n .v .c, .metrics-banner .status-count.on .n .v .c { color: rgba(255, 255, 255, 0.82); }
  .metrics-banner .status-count .nm {
    font-size: 0.625rem; letter-spacing: 0.3px; text-transform: uppercase; font-weight: 600;
    color: rgba(255, 255, 255, 0.6); white-space: nowrap; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
  }
  /* Selected or hovered reads as full white; the rest sit back without changing size, so nothing moves. */
  .metrics-banner .status-count:hover .n, .metrics-banner .status-count.on .n { color: ${css.white}; }
  .metrics-banner .status-count:hover .nm, .metrics-banner .status-count.on .nm { color: rgba(255, 255, 255, 0.9); }
  .metrics-banner .status-count .dot { width: 0.45rem; height: 0.45rem; border-radius: 50%; flex: none; box-shadow: none; }
  .view-area { flex: 1; min-height: 0; }
  .placeholder { padding: 3rem; text-align: center; color: ${css.grey4}; font-size: 1.1rem; }
`;

const WideModalStyled = styled(ModalStyled)`
  .modal-window { width: 45rem; max-width: 100%; }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.1rem 0; }
  .data-room-types { margin: 0.75rem 0; }
  .type-row { display: flex; gap: 0.5rem; align-items: center; padding: 0.15rem 0; cursor: pointer; }
  .file-pick { display: block; margin: 0.75rem 0; }
  .batch-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .batch-table th, .batch-table td { text-align: left; padding: 0.25rem 0.4rem; border-bottom: 1px solid ${css.grey6}; }
  .modal-buttons { display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1rem; }
`;

const VIEWS = ['table', 'timeline', 'finances', 'map'] as const;
type ViewKey = typeof VIEWS[number];
// Views kept in the code but off the tab strip. A stored 'pm.view' of an unknown key (e.g. the removed
// 'board') falls back to the table, so nobody lands on a blank pane.
const HIDDEN_VIEWS: ViewKey[] = [];
// The 'timeline' KEY is persisted in localStorage ('pm.view') and matched on below, so only the
// label changes here — renaming the key would silently reset everyone's saved view to the table.
const viewLabel: Record<ViewKey, () => string> = {
  table: strings.Table, timeline: strings.Schedule, finances: strings.Finances, map: strings.Map,
};
/** New-project draft seed. */
const emptyDraft = (): Partial<ProjectManagementProject> => ({ _id: 'new', active: true, lifecycleKey: projectManagementDefaultLifecycleKey });

/**
 * Data-room export dialog: pick document types (from the lifecycle's slots) to batch-export across the
 * currently filtered project set as a zip, one folder per project.
 */
const DataRoomModal = (props: { lifecycle: ProjectManagementLifecycle; rows: ProjectManagementPipelineRow[]; onClose: () => void }) => {
  const { lifecycle, rows, onClose } = props;
  const [selected, setSelected] = useState<{ [docType: string]: boolean }>({});
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [exporting, setExporting] = useState(false);
  // Dedupe by docType (a lifecycle may declare the same type in more than one stage).
  const slots = lifecycle.docSlots.filter((s, i) => lifecycle.docSlots.findIndex((o) => o.docType === s.docType) === i);
  const docTypes = Object.keys(selected).filter((k) => selected[k]);
  const doExport = async () => {
    setExporting(true);
    try {
      const res = await projectManagementDataRoomExport.call({
        projectManagementProjectIds: rows.map((r) => r.project._id), docTypes, approvedOnly,
      });
      toast.info(`${res.files} ${strings.Documents()} · ${strings.Skipped()}: ${res.skipped}`);
      window.open(fileHref(res) || res.url, '_blank');
      onClose();
    }
    catch (error: any) {
      toast.error(`${error?.message || error}`);
    }
    finally {
      setExporting(false);
    }
  };
  return (
    <Modal styled={WideModalStyled} title={strings.DataRoom()} onClose={onClose}>
      <div className="meta-row">{rows.length} {strings.Projects()}</div>
      <div className="data-room-types">
        <div className="meta-row">{strings.DocumentTypes()}</div>
        {
          slots.map((s) => (
            <label className="type-row" key={s.docType}>
              <input type="checkbox" checked={!!selected[s.docType]} onChange={(e) => setSelected({ ...selected, [s.docType]: e.target.checked })} />
              {s.label}
            </label>
          ))
        }
      </div>
      <label className="type-row">
        <input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />
        {strings.ApprovedVersionsOnly()}
      </label>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => doExport()} disabled={!docTypes.length || !rows.length || exporting}>{strings.Export()}</Button>
      </div>
    </Modal>
  );
};

interface BatchFile {
  file: File;
  projectManagementProjectId: string;  // '' when unmatched — the user must pick before it uploads
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

/**
 * Batch-upload dialog: drop many files into ONE docType slot across the filtered project set. Files
 * auto-match to projects by externalId/name appearing in the filename, with a per-file override.
 */
const BatchUploadModal = (props: { lifecycle: ProjectManagementLifecycle; rows: ProjectManagementPipelineRow[]; onClose: () => void }) => {
  const { lifecycle, rows, onClose } = props;
  const slots = lifecycle.docSlots.filter((s, i) => lifecycle.docSlots.findIndex((o) => o.docType === s.docType) === i);
  const [docType, setDocType] = useState(slots[0]?.docType || '');
  const [files, setFiles] = useState<BatchFile[]>([]);
  const [busy, setBusy] = useState(false);
  const projectOptions = [{ value: '', label: '—' }, ...rows.map((r) => ({ value: r.project._id, label: r.project.name }))];

  /** Best-effort filename → project match: luminiaId (unique everywhere), then externalId, then full name. */
  const matchProject = (fileName: string): string => {
    const name = fileName.toLowerCase();
    const byLuminia = rows.find((r) => r.project.luminiaId && name.includes(r.project.luminiaId.toLowerCase()));
    if (byLuminia) return byLuminia.project._id;
    const byExternal = rows.find((r) => r.project.externalId && name.includes(r.project.externalId.toLowerCase()));
    if (byExternal) return byExternal.project._id;
    const byName = rows.find((r) => r.project.name && name.includes(r.project.name.toLowerCase()));
    return byName?.project._id || '';
  };
  // Takes an ARRAY, not the input's live FileList: setFiles defers its updater, and the caller clears
  // e.target.value as soon as it returns, which emptied that FileList before the updater ever read it —
  // so a pick after the dialog's first state change silently landed nothing.
  const addFiles = (picked: File[]) => {
    if (!picked.length) return;
    setFiles((fs) => [...fs, ...picked.map((file): BatchFile => ({ file, projectManagementProjectId: matchProject(file.name), status: 'pending' }))]);
  };
  const patchFile = (index: number, patch: Partial<BatchFile>) => setFiles((fs) => fs.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  /** Sequentially upload every matched pending file into its project's slot; unmatched files are skipped. */
  const uploadAll = async () => {
    setBusy(true);
    let done = 0;
    let skipped = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f.status === 'done') continue;
      if (!f.projectManagementProjectId) {
        skipped++;
        continue;
      }
      patchFile(i, { status: 'uploading' });
      try {
        const meta = await uploadFile(f.file);
        await projectManagementBatchUploadDocument.call({ projectManagementProjectId: f.projectManagementProjectId, docType, file: meta });
        patchFile(i, { status: 'done' });
        done++;
      }
      catch (error: any) {
        patchFile(i, { status: 'error', error: `${error?.message || error}` });
        skipped++;
      }
    }
    setBusy(false);
    toast.info(`${done} ${strings.Documents()} · ${strings.Skipped()}: ${skipped}`);
  };

  const statusMark: { [k in BatchFile['status']]: string } = { pending: '·', uploading: '…', done: '✓', error: '✗' };
  return (
    <Modal styled={WideModalStyled} title={strings.BatchUpload()} onClose={onClose}>
      <div className="meta-row">{strings.DocumentTypes()}</div>
      <Select value={docType} options={slots.map((s) => ({ value: s.docType, label: s.label }))} onChange={(v) => setDocType(v)} />
      <label className="file-pick">
        <input type="file" multiple onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} disabled={busy} />
      </label>
      {
        files.length > 0 &&
          <table className="batch-table">
            <thead><tr><th>{strings.Files()}</th><th>{strings.Projects()}</th><th></th></tr></thead>
            <tbody>
              {
                files.map((f, i) => (
                  <tr key={`${f.file.name}-${i}`}>
                    <td>{f.file.name}<span className="meta-row"> · {formatBytes(f.file.size)}</span></td>
                    <td>
                      <Select value={f.projectManagementProjectId} options={projectOptions} onChange={(v) => patchFile(i, { projectManagementProjectId: v })} />
                    </td>
                    <td>{statusMark[f.status]}{f.error ? ` ${f.error}` : ''}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
      }
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => uploadAll()} disabled={busy || !docType || !files.some((f) => f.status === 'pending' && f.projectManagementProjectId)}>{strings.Upload()}</Button>
      </div>
    </Modal>
  );
};

const PIPELINE_SCHEDULE_MODE_KEY = 'pm.pipeline.scheduleMode';
const PIPELINE_SCHEDULE_DETAIL_KEY = 'pm.pipeline.scheduleDetail';
/** Milestones the schedule keeps for "more detail" only: the PPA lands before construction and crowds a
 *  strip whose story is LNTP → Final. Matched on the key prefix, so every vertical's variant counts
 *  (RIC's ppaExecuted, CEA's ppaEssaExecuted). */
const scheduleDetailMilestone = (m: { key?: string }) => /^ppa/i.test(m.key || '');

const PipelineTimelineStyled = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  .mode-toggle { flex: none; display: flex; align-items: center; gap: 0.3rem; padding: 0.5rem 0; }
  /* The detail checkbox sits at the left; the mode buttons stay right whether or not it is rendered. */
  .mode-toggle .modes { display: flex; gap: 0.3rem; margin-left: auto; }
  /* The shared Checkbox baseline-aligns a 1.5rem box against its text and raises it slightly, which reads
     as crooked beside this row's smaller label — centre the two instead, here only. */
  .mode-toggle label { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; color: ${css.grey2}; cursor: pointer; }
  .mode-toggle label input { top: 0; margin: 0; flex: none; }
  .mode-toggle button { padding: 0.3rem 0.9rem; border: ${css.inputBorder}; background: ${css.white}; color: ${css.grey2}; border-radius: 4px; cursor: pointer; font-size: 0.85rem; font-weight: 600; }
  .mode-toggle button.active { background: ${css.primaryColor}; color: ${css.white}; border-color: ${css.primaryColor}; }
  .view-wrap { flex: 1; min-height: 0; }
`;

/** Pipeline timeline with a sticky Gantt/Calendar toggle. Admins plot milestones, members their own tasks
 *  (taskMode); the calendar spans every project, so each entry is prefixed with its project name. */
const PipelineTimeline = ({ rows, lifecycle, taskMode }: { rows: ProjectManagementPipelineRow[]; lifecycle: ProjectManagementLifecycle; taskMode: boolean }) => {
  const [mode, setMode] = useState<'gantt' | 'calendar'>(() => {
    try { return localStorage.getItem(PIPELINE_SCHEDULE_MODE_KEY) === 'calendar' ? 'calendar' : 'gantt'; }
    catch (_) { return 'gantt'; }
  });
  const select = (m: 'gantt' | 'calendar') => {
    setMode(m);
    try { localStorage.setItem(PIPELINE_SCHEDULE_MODE_KEY, m); }
    catch (_) { /* private mode — the choice just won't persist */ }
  };
  const [detail, setDetail] = useState(() => {
    try { return localStorage.getItem(PIPELINE_SCHEDULE_DETAIL_KEY) === '1'; }
    catch (_) { return false; }
  });
  const selectDetail = (on: boolean) => {
    setDetail(on);
    try { localStorage.setItem(PIPELINE_SCHEDULE_DETAIL_KEY, on ? '1' : '0'); }
    catch (_) { /* private mode — the choice just won't persist */ }
  };
  // Off, the schedule carries the construction milestones alone (LNTP → Final). A member's schedule IS
  // their own tasks, so there is nothing to thin out there and no checkbox to offer.
  const thin = !taskMode && !detail;
  const shown = thin
    ? rows.map((r) => ({ ...r, tasks: [], milestones: r.milestones.filter((m) => !scheduleDetailMilestone(m)) }))
    : rows;
  const nameById = new Map(rows.map((r) => [r.project._id, r.project.name]));
  return (
    <PipelineTimelineStyled>
      <div className="mode-toggle">
        {!taskMode && <Checkbox checked={detail} onChange={(on) => selectDetail(on)}>{strings.ShowMoreDetail()}</Checkbox>}
        <div className="modes">
          <button className={mode === 'gantt' ? 'active' : ''} onClick={() => select('gantt')}>{strings.Gantt()}</button>
          <button className={mode === 'calendar' ? 'active' : ''} onClick={() => select('calendar')}>{strings.Calendar()}</button>
        </div>
      </div>
      <div className="view-wrap">
        {
          mode === 'gantt'
            // The Gantt plots milestones + tasks for everyone; taskMode still drives the Calendar's data.
            ? <ProjectManagementGanttView rows={shown} lifecycle={lifecycle} />
            : <ProjectManagementScheduleView
                tasks={taskMode ? shown.flatMap((r) => r.tasks || []) : []}
                milestones={taskMode ? [] : shown.flatMap((r) => r.milestones)}
                projectName={(id) => nameById.get(id)}
              />
        }
      </div>
    </PipelineTimelineStyled>
  );
};

/** Project Management workspace: a filtered project set rendered through a switchable view. */
export const ProjectManagementPipelinePage = () => {
  const navigate = useNavigate();
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const canAccess = projectManagementCanAccess(user, account);
  // Mirrors the server tiers: members (external stakeholder accounts) get a slim workspace — their
  // projects in table/timeline form, no financials, no portfolios, no create/batch/data-room tooling.
  const isAdmin = projectManagementIsAdmin(user, account);
  const canCreateProject = projectManagementCanCreateProject(user, account);
  const canSeePortfolioFees = projectManagementCanSeeFees(user, account);
  // Persisted like the view choice: the page unmounts on every project-detail visit, and snapping back
  // to the default lifecycle would hide the book the user was working (e.g. a project they just created).
  const [lifecycleKey, setLifecycleKey] = useState(projectManagementWorkspaceLifecycle);
  const selectLifecycle = (key: string) => {
    setLifecycleKey(key);
    projectManagementSetWorkspaceLifecycle(key);
  };
  const [portfolioId, setPortfolioId] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('pm.portfolio')) || '');
  const selectPortfolio = (id: string) => {
    setPortfolioId(id);
    try { localStorage.setItem('pm.portfolio', id); }
    catch (_) { /* private mode */ }
  };
  // Held projects stay in the list rather than disappearing from it, so the filter is how someone reads
  // the list as "what is actually moving" without the counts quietly changing underneath them.
  const [stateFilter, setStateFilter] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('pm.state')) || '');
  const [statusFilter, setStatusFilter] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('pm.status')) || '');
  const selectStatus = (value: string) => {
    setStatusFilter(value);
    try { localStorage.setItem('pm.status', value); }
    catch (_) { /* private mode */ }
  };
  // Where the site is, read out of the freeform address — projects carry no structured state field.
  const selectState = (value: string) => {
    setStateFilter(value);
    try { localStorage.setItem('pm.state', value); }
    catch (_) { /* private mode */ }
  };
  const [showNewPortfolio, setShowNewPortfolio] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const createPortfolio = toastOnError(async () => {
    if (!newPortfolioName.trim()) { toast.info(strings.Name()); return; }
    const id = await projectManagementSavePortfolio.call({ name: newPortfolioName.trim() });
    selectPortfolio(id);
    setShowNewPortfolio(false);
    setNewPortfolioName('');
  });
  const [view, setView] = useState<ViewKey>(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('pm.view') as ViewKey : null;
    return saved && VIEWS.includes(saved) ? saved : 'table';
  });
  // The search box persists like the selects above it: coming back to a filtered list and finding it
  // unfiltered is the same surprise either way, and the term stays visible in the box.
  const [search, setSearchValue] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('pm.search')) || '');
  const setSearch = (value: string) => {
    setSearchValue(value);
    try { localStorage.setItem('pm.search', value); }
    catch (_) { /* private mode */ }
  };
  const [showCreate, setShowCreate] = useState(false);
  const [showDataRoom, setShowDataRoom] = useState(false);
  const [showBatchUpload, setShowBatchUpload] = useState(false);
  const [draft, setDraft] = useState<Partial<ProjectManagementProject>>(emptyDraft());

  const lifecycle = getProjectManagementLifecycle(lifecycleKey);
  // Every vertical: what the New Project dialog offers, so a vertical with no projects yet can still get its first.
  const lifecycleOptions = Object.values(projectManagementLifecycles).map((l) => ({ value: l.key, label: l.label }));
  // The workspace selector offers only the verticals this user has projects in — plus the one currently
  // selected, which must stay in the list for the control to render its own value. Re-read on each switch,
  // which is also how a vertical that just received its first project appears.
  const [lifecyclesInUse] = useMethod(projectManagementLifecyclesInUse, undefined, [lifecycleKey]);
  const workspaceLifecycleOptions = lifecycleOptions.filter((o) => o.value === lifecycleKey || (lifecyclesInUse || []).includes(o.value));

  // Members have no Vertical filter (the controls below), so they subscribe to every vertical they can see
  // — a handful of projects — and the page lands on one that actually holds their projects rather than the
  // persisted or default vertical, which they could not switch away from. Decided once the account has
  // loaded, so an admin never briefly pulls every vertical.
  const memberScope = !!account && !isAdmin;
  const subscriptionKey = memberScope ? undefined : lifecycleKey;

  // Reactive, in two stages: projects paint as soon as their (small) subscription is ready, while the
  // heavier per-project data (milestones + cash) streams in behind and fills the columns when it lands.
  // Rows recompose from Minimongo on any change (a teammate's stage move, a cash edit, a geocode backfill).
  const loading = useSubscribe(Publications.projectManagementPipelineProjects, subscriptionKey);
  const detailsLoading = useSubscribe(Publications.projectManagementPipelineDetails, subscriptionKey);
  const memberProjects = useFind(() => ProjectManagementProjects.find(memberScope ? { active: { $ne: false } } : { _id: '__none__' }, { fields: { lifecycleKey: 1 } }), [memberScope]);
  const memberVerticals = Object.keys(projectManagementLifecycles).filter((k) => memberProjects.some((p) => p.lifecycleKey === k));
  useEffect(() => {
    if (memberScope && memberVerticals.length && !memberVerticals.includes(lifecycleKey)) selectLifecycle(memberVerticals[0]);
  }, [memberScope, memberVerticals.join(','), lifecycleKey]);
  const portfolios = useTracker(() => ProjectManagementPortfolios.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  // A remembered filter can outlive its portfolio: deleting the one the workspace was filtered on left the
  // dropdown falling back to "All" while the filter still excluded every project, so the page read 0 of 124.
  // Only once the portfolios have arrived, or an empty list would clear a perfectly good filter on first paint.
  useEffect(() => {
    if (loading() || !portfolioId) return;
    if (!portfolios.some((p) => p._id === portfolioId)) selectPortfolio('');
  }, [loading(), portfolioId, portfolios.map((p) => p._id).join(',')]);
  // Owner / EPC / utility registries, so New Project offers the same parties the project page's Edit does.
  // The pipeline publication already sends all three to admins.
  const owners = useTracker(() => ProjectManagementOwners.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const epcs = useTracker(() => ProjectManagementEpcs.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const utilities = useTracker(() => ProjectManagementUtilities.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const rows = useTracker<ProjectManagementPipelineRow[]>(() => {
    const projects = ProjectManagementProjects.find({ lifecycleKey, active: { $ne: false } }).fetch();
    // Portfolio names, for the default row order below: the registry rides along on this publication.
    const portfolioNames = new Map(ProjectManagementPortfolios.find({}).fetch().map((p) => [p._id, p.name || '']));
    // Group children once (O(docs)) instead of a Minimongo find per project (O(projects × docs)).
    const milestonesByProject = new Map<string, ProjectManagementMilestone[]>();
    for (const m of ProjectManagementMilestones.find({}, { sort: { order: 1 } }).fetch()) {
      const list = milestonesByProject.get(m.projectManagementProjectId) || [];
      list.push(m);
      milestonesByProject.set(m.projectManagementProjectId, list);
    }
    const cashByProject = new Map<string, ProjectManagementCashEvent[]>();
    for (const c of ProjectManagementCashEvents.find({}).fetch()) {
      const list = cashByProject.get(c.projectManagementProjectId) || [];
      list.push(c);
      cashByProject.set(c.projectManagementProjectId, list);
    }
    // Only published to a vertical whose Sources are read from them, and only to a caller allowed the
    // figure — so on RIC this map is empty and every row falls through to the ledger.
    const sourcesByProject = new Map<string, ProjectManagementFundingSource[]>();
    for (const f of ProjectManagementFundingSources.find({}).fetch()) {
      const list = sourcesByProject.get(f.projectManagementProjectId) || [];
      list.push(f);
      sourcesByProject.set(f.projectManagementProjectId, list);
    }
    // Tasks only reach members (for the 'Next Task' column); on an admin's pipeline Minimongo has none.
    const tasksByProject = new Map<string, ProjectManagementTask[]>();
    for (const t of ProjectManagementTasks.find({}).fetch()) {
      const list = tasksByProject.get(t.projectManagementProjectId) || [];
      list.push(t);
      tasksByProject.set(t.projectManagementProjectId, list);
    }
    // Only the Total Fees parameters are published to the pipeline (admins only; members get none).
    const parametersByProject = new Map<string, ProjectManagementParameter[]>();
    for (const p of ProjectManagementParameters.find({}).fetch()) {
      const list = parametersByProject.get(p.projectManagementProjectId) || [];
      list.push(p);
      parametersByProject.set(p.projectManagementProjectId, list);
    }
    // Status, then portfolio alphabetically, then name — one stable order every view inherits, so
    // returning to the list shows what you left. Was `updatedAt`, which reshuffled under the reader
    // whenever anyone (or a job) touched a project.
    const fromSources = projectManagementSourcesFigure(lifecycle) === 'fundingSources';
    const feeUseCodes = projectManagementFeeUseCodes(lifecycle);
    return projects.map((project) => {
      const cashEvents = cashByProject.get(project._id) || [];
      return {
        project,
        tasks: tasksByProject.get(project._id) || [],
        milestones: milestonesByProject.get(project._id) || [],
        // Per row, so the portfolio total is the sum of what each project's own banner reports.
        cash: projectManagementFinancialSummary(projectManagementUsesLedger(cashEvents, lifecycle?.finance), fromSources
          ? { inEstimated: projectManagementFundingSourcesEstimate(sourcesByProject.get(project._id) || []) }
          : undefined),
        // Per row again, so the portfolio's Fees tile is the sum of what each project's own banner reports.
        fees: feeUseCodes
          ? projectManagementUseFees(cashEvents, feeUseCodes)
          : projectManagementCompleteProjectFees(projectManagementTotalFees(parametersByProject.get(project._id) || [], cashEvents), project, cashEvents),
      };
    }).sort((a, b) => {
      const byStatus = projectManagementStatusOrder[projectManagementProjectStatus(a.project)]
        - projectManagementStatusOrder[projectManagementProjectStatus(b.project)];
      if (byStatus) return byStatus;
      // Unassigned projects sit after every named portfolio rather than ahead of 'A'.
      const aHas = a.project.portfolioId ? 0 : 1;
      const bHas = b.project.portfolioId ? 0 : 1;
      if (aHas !== bHas) return aHas - bHas;
      const byPortfolio = (portfolioNames.get(a.project.portfolioId || '') || '')
        .localeCompare(portfolioNames.get(b.project.portfolioId || '') || '', undefined, { sensitivity: 'base' });
      if (byPortfolio) return byPortfolio;
      return (a.project.name || '').localeCompare(b.project.name || '', undefined, { sensitivity: 'base' });
    });
  }, [lifecycleKey]);

  // Finances is for finance viewers (super users, or accounting + PM) — not the admin tier; the rest of
  // the admin-only views (map) stay with admins, and everyone gets table + timeline.
  // The Finances view is the portfolio's cash, project by project: super users and invoice approvers only
  // (2026-10-20), the same rule as the money tiles in the banner above it. Accounting keeps the per-project
  // Budget tab; what it no longer gets is the org-wide roll-up. A saved or linked 'finances' view falls back
  // to the table through effectiveView below, so hiding the tab is also the gate.
  const permittedViews: ViewKey[] = VIEWS.filter((v) => (v === 'finances' ? canSeePortfolioFees : isAdmin || v === 'table' || v === 'timeline'));
  const availableViews = permittedViews.filter((v) => !HIDDEN_VIEWS.includes(v));
  const effectiveView = availableViews.includes(view) ? view : 'table';
  // The states this workspace actually builds in, not a list of fifty. A filter whose stored value no
  // longer matches any project still appears, so the list cannot silently show everything while the box
  // reads a state.
  const stateOptions = [...new Set(rows.map((r) => projectManagementStateFromAddress(r.project.address)).filter(Boolean) as string[])].sort();
  if (stateFilter && !stateOptions.includes(stateFilter)) stateOptions.push(stateFilter);
  const query = search.replace(/\s+/g, ' ').trim().toLowerCase();
  const portfolioRows = portfolioId ? rows.filter((r) => r.project.portfolioId === portfolioId) : rows;
  const stateRows = stateFilter
    ? portfolioRows.filter((r) => projectManagementStateFromAddress(r.project.address) === stateFilter)
    : portfolioRows;
  const statusRows = statusFilter
    ? stateRows.filter((r) => projectManagementProjectStatus(r.project) === statusFilter)
    : stateRows;
  const filteredRows = query
    ? statusRows.filter((r) => `${r.project.name || ''} ${r.project.address || ''} ${r.project.externalId || ''} ${r.project.luminiaId || ''}`.replace(/\s+/g, ' ').toLowerCase().includes(query))
    : statusRows;
  const summary = projectManagementPipelineSummary(filteredRows);
  // Every status, every time, zero included: "Installed 0kW" is the answer to "how much is installed", and a
  // figure that appears and disappears with the data cannot be scanned for in a fixed place. Clicking one
  // filters to it; clicking it again clears (so does the Status box above). 'deferred' is not reported here
  // — the four below are the ones asked for; a deferred project's size is reachable through the Status
  // filter instead.
  // This is a REQUESTED reading order, not the lifecycle's: Installed and In Progress on the top row, the
  // two that have stopped beneath them. It is laid out two to a row, so the array order IS the grid order.
  const statusBreakdown: ProjectManagementProjectStatus[] = ['complete', 'moving', 'onHold', 'canceled'];
  // Sizes step up a unit per thousand, so a portfolio reads 21.08MW rather than 21077kW. Two decimals at
  // most, with no trailing zeroes padding them out: 7.2kW, 16kWh, 21.08MW.
  const sizeText = (n: number, tier: number, units: string[]) =>
    `${formatNumber(n / (1000 ** tier), 2).replace(/\.?0+$/, '')}${units[tier]}`;
  // One unit for the whole tile per quantity, taken from its largest figure, rather than one per cell: the
  // four statuses are here to be compared, and 500kW sitting beside 21.08MW cannot be read at a glance.
  const tierOf = (values: number[]) => { const max = Math.max(0, ...values); return max >= 1e6 ? 2 : max >= 1e3 ? 1 : 0; };
  const powerTier = tierOf(statusBreakdown.map((st) => summary.capacityByStatus[st].kwDc));
  const energyTier = tierOf(statusBreakdown.map((st) => summary.capacityByStatus[st].kwh));
  // Storage is all-or-nothing across the tile: one status carrying it gives every cell the second figure,
  // and a portfolio with no storage at all shows none rather than four "/0kWh".
  const showStorage = statusBreakdown.some((st) => summary.capacityByStatus[st].kwh > 0);
  const sizeOfStatus = (st: ProjectManagementProjectStatus) => {
    const carried = summary.capacityByStatus[st];
    const power = sizeText(carried.kwDc, powerTier, [strings.UnitKw(), strings.UnitMw(), strings.UnitGw()]);
    if (!showStorage) return power;
    // The zero-width space is the pair's only break opportunity: a column too narrow for the whole string
    // wraps after the slash rather than spilling into the status beside it.
    return `${power}/\u200b${sizeText(carried.kwh, energyTier, [strings.UnitKwh(), strings.UnitMwh(), strings.UnitGwh()])}`;
  };
  // In this tile the four labels name a CAPACITY bucket rather than a project status, which is what the
  // figures beside them are read for: "Installed 21.08MW". The Status filter above, the badge in the table
  // below and the exports all keep the status's own name, so only these four cells are reworded/recoloured.
  const sizeLabel = (st: ProjectManagementProjectStatus) => (st === 'complete' ? strings.Installed() : projectStatusLabel(st));
  // Energised capacity reads green, and green3 is already what "done" looks like in this module (a finished
  // task's border, a saved cell). The light shade is the point: beside moving's dark green on a 7px dot it
  // is lightness, not hue, that keeps the two buckets apart.
  const sizeDot = (st: ProjectManagementProjectStatus) => (st === 'complete' ? css.green3 : projectStatusColor[st]);
  // Only a vertical that asks for the review tiles gets them, and the roll-up is not computed otherwise.
  // Kept out of the finance banner above because a reviewer is not necessarily a finance viewer.
  const financeModel = projectManagementFinanceModel(lifecycle);
  const review = lifecycle?.pipelineReviewSummary ? projectManagementReviewSummary(filteredRows) : undefined;

  const selectView = (v: ViewKey) => {
    setView(v);
    try { localStorage.setItem('pm.view', v); }
    catch (_) { /* private mode */ }
  };

  const [newProjectForm] = autoForm<ProjectManagementProject>({
    createMethod: projectManagementCreateProject,
    update: draft,
    setUpdate: setDraft,
    defaultUpdate: emptyDraft(),
    submitButtonText: strings.Create(),
    // Two per row, like the project page's Edit modal — the same fields in the same order, so switching
    // between creating and editing does not mean re-finding everything. A .field-group is already a flex
    // row that splits evenly and collapses to one column on mobile, so pairing is all this takes. Address
    // keeps a row to itself: the autocomplete panel needs the width.
    form: (): FormSpec<ProjectManagementProject> => [
      [
        { field: 'lifecycleKey', label: strings.Vertical(), input: { type: Select, props: { options: lifecycleOptions } } },
        { field: 'portfolioId', label: strings.Portfolio(), optional: true, input: { type: Select, props: { options: [{ value: '', label: '—' }, ...portfolios.map((p) => ({ value: p._id, label: p.name }))] } } },
      ],
      [
        { field: 'name', label: `${strings.Customer()} / ${strings.Name()}`, asterisk: true },
        { field: 'logo', optional: true, input: (setUpdate, update) => (
          <FormField key="logo" field="logo">
            <label>{strings.ProjectLogo()}</label>
            <ImageInput value={update.logo} accept="image/*" onChange={(logo) => setUpdate({ ...update, logo })} />
          </FormField>
        ) },
      ],
      [{ field: 'address', optional: true, input: (setUpdate, update) => (
        <FormField key="address" field="address">
          <label>{strings.Address()}</label>
          <AddressInput inline onChange={(address) => setUpdate({ ...update, address: address?.formatted, lat: address?.location?.lat, lng: address?.location?.lng })} />
        </FormField>
      ) }],
      [
        { field: 'ownerId', optional: true, input: (setUpdate, update) => (
          <FormField key="ownerId" field="ownerId">
            <label>{strings.Owner()}</label>
            <PartyPicker modelClass={projectManagementOwnerModelClass} parties={owners} value={update.ownerId}
              onChange={(ownerId) => setUpdate({ ...update, ownerId })} />
          </FormField>
        ) },
        { field: 'epcId', optional: true, input: (setUpdate, update) => (
          <FormField key="epcId" field="epcId">
            <label>{strings.Epc()}</label>
            <PartyPicker modelClass={projectManagementEpcModelClass} parties={epcs} value={update.epcId}
              onChange={(epcId) => setUpdate({ ...update, epcId })} />
          </FormField>
        ) },
      ],
      // Capacity/storage matter at creation: they select the schedule tier (e.g. FTM fast-track vs
      // CAISO cluster), which is pinned when the milestones are instantiated.
      [
        { field: 'capacityKwDc', label: strings.CapacityKwDc(), optional: true, input: { type: NumberInput } },
        { field: 'storageKwh', label: strings.StorageKwh(), optional: true, input: { type: NumberInput } },
      ],
      [
        { field: 'onsiteContactName', label: strings.OnsiteContactName(), optional: true },
        { field: 'onsiteEmail', label: strings.OnsiteEmail(), optional: true },
      ],
      [
        { field: 'onsitePhone', label: strings.OnsitePhone(), optional: true, input: { type: PhoneInput } },
        { field: 'clientId', label: strings.ClientId(), optional: true },
      ],
      [
        { field: 'utilityId', optional: true, input: (setUpdate, update) => (
          <FormField key="utilityId" field="utilityId">
            <label>{strings.UtilityCompany()}</label>
            <PartyPicker modelClass={projectManagementUtilityModelClass} parties={utilities} value={update.utilityId}
              onChange={(utilityId) => setUpdate({ ...update, utilityId })} />
          </FormField>
        ) },
        { field: 'sgipId', label: strings.SgipId(), optional: true },
      ],
      [{ field: 'externalId', label: projectManagementExternalIdLabel(draft.lifecycleKey), optional: true }],
    ],
    onSave: (id) => {
      // Follow the created project's lifecycle, so the workspace shows it when the user comes back.
      if (draft.lifecycleKey) selectLifecycle(draft.lifecycleKey);
      setShowCreate(false);
      setDraft(emptyDraft());
      navigate(`/project-management/projects/${id}`);
    },
    extraButtons: <Button className="btn secondary" onClick={() => setShowCreate(false)}>{strings.Cancel()}</Button>,
  });

  if (!canAccess) return <NotPermittedPage />;
  // No incremental paint: hold the whole page on one spinner until BOTH subscriptions — the project
  // spines AND the heavier per-project details (milestones/cash) — have fully landed.
  if (loading() || detailsLoading()) {
    return (
      <ProjectManagementPipelinePageStyled>
        <PageHeading left={<h1>{strings.ProjectManagement()}</h1>} />
        <PageBody><LoadingFlex /></PageBody>
      </ProjectManagementPipelinePageStyled>
    );
  }
  return (
    <ProjectManagementPipelinePageStyled>
      <PageHeading
        left={<h1>{strings.ProjectManagement()}</h1>}
        right={
          isAdmin &&
          <div className="heading-buttons">
            <Link to="/project-management/portfolios"><Button className="secondary">{strings.Portfolios()}</Button></Link>
            {isAdmin && <Link to="/project-management/bulk"><Button className="secondary">{strings.BulkEdit()}</Button></Link>}
            <Button className="secondary" onClick={() => setShowBatchUpload(true)}>{strings.BatchUpload()}</Button>
            <Button className="secondary" onClick={() => setShowDataRoom(true)}>{strings.DataRoom()}</Button>
            {canCreateProject && <Button className="primary" onClick={() => { setDraft({ ...emptyDraft(), lifecycleKey, portfolioId: portfolioId || undefined }); setShowCreate(true); }}>{strings.New()}</Button>}
          </div>
        }
      />
      <PageBody>
        <div className="controls">
          {/* Admins only (2026-10-01). Shown again for them 2026-09-23: hiding it made the persisted lifecycle
              a one-way door, since the create form writes it and nothing read it back. A member cannot
              create, and lands on a vertical holding their projects (memberVerticals), so they need no door. */}
          {isAdmin && <span>{strings.Vertical()}:</span>}
          {isAdmin &&
            <div className="lifecycle-select">
              <Select value={lifecycleKey} options={workspaceLifecycleOptions} onChange={(v) => selectLifecycle(v)} />
            </div>
          }
          {isAdmin && <span>{strings.Portfolio()}:</span>}
          {isAdmin &&
            <div className="portfolio-select">
              <Select value={portfolioId} options={[{ value: '', label: strings.All() }, ...portfolios.map((p) => ({ value: p._id, label: p.name }))]} onChange={(v) => selectPortfolio(v)} />
            </div>
          }
          {isAdmin && <Button className="primary new-portfolio" tip={strings.AddNewPortfolio()} onClick={() => setShowNewPortfolio(true)}>+</Button>}
          <span>{strings.State()}:</span>
          <div className="status-select">
            <Select
              value={stateFilter}
              options={[{ value: '', label: strings.All() }, ...stateOptions.map((st) => ({ value: st, label: st }))]}
              onChange={(v) => selectState(v)}
            />
          </div>
          <span>{strings.Status()}:</span>
          <div className="status-select">
            <Select
              value={statusFilter}
              options={[{ value: '', label: strings.All() }, ...projectManagementProjectStatuses.map((st) => ({ value: st, label: projectStatusLabel(st) }))]}
              onChange={(v) => selectStatus(v)}
            />
          </div>
          <div className="search"><Input value={search} onChange={(v) => setSearch(v || '')} placeholder={strings.Search()} /></div>
        </div>
        {/* The project count and the installed size behind it are for everyone; every money tile beside them
            is the org-wide roll-up, and those are super users and invoice approvers only (2026-10-20) — neither
            a PM admin nor accounting reads the portfolio's cash off the top of the page. The Table and
            Finances views below still carry the per-project figures for finance viewers. */}
        <ProjectManagementMetricsBanner className="large-sub" metrics={[
          {
            dot: projectManagementMetricDot(0),
            label: strings.Projects(),
            className: 'projects',
            // The count rides on the label row, which leaves the whole value area to the four sizes — they
            // get the width to be read at a glance, and the count still outranks them from above.
            headValue: formatNumber(summary.totalProjects),
            value: (
              <span className="status-split">
                {
                  statusBreakdown.map((st) => (
                    <button
                      key={st}
                      type="button"
                      className={`status-count${st === statusFilter ? ' on' : ''}`}
                      onClick={() => selectStatus(st === statusFilter ? '' : st)}
                    >
                      <span className="n">
                        <span className="dot" style={{ background: sizeDot(st) }} />
                        <span className="v">{sizeOfStatus(st)} <span className="c">({formatNumber(summary.countByStatus[st])})</span></span>
                      </span>
                      <span className="nm">{sizeLabel(st)}</span>
                    </button>
                  ))
                }
              </span>
            ),
          },
          // The partner's language throughout: an estimate is the Project Payment, an actual is what has been invoiced.
          ...(canSeePortfolioFees ? [
            { dot: projectManagementMetricDot(1), label: strings.CashSources(), value: money(summary.cash.inEstimated), sub: `${strings.Invoiced()}: ${money(summary.cash.inActual)}` },
            { dot: projectManagementMetricDot(2), label: strings.CashUses(), value: money(summary.cash.outEstimated), sub: `${strings.Invoiced()}: ${money(summary.cash.outActual)}` },
          ] : []),
          // The Luminia development fee belongs to the RIC money model; a vertical without one shows no tile.
          // Luminia's own margin across the portfolio: super users and invoice approvers only, and the
          // parameters it sums are withheld from everyone else, so there is no figure to render anyway.
          ...((financeModel === 'ric' || !!projectManagementFeeUseCodes(lifecycle)) && canSeePortfolioFees ? [{ dot: projectManagementMetricDot(3), label: strings.TotalFees(), value: money(summary.fees.estimated), sub: `${strings.Actual()}: ${money(summary.fees.actual)}` }] : []),
        ]} />
        {isAdmin && review &&
        <ProjectManagementMetricsBanner className="large-sub" metrics={[
          { dot: projectManagementMetricDot(0), label: strings.AwaitingReview(), value: review.awaiting },
          { dot: projectManagementMetricDot(1), label: strings.OpenDeficiencies(), value: review.deficiencies },
          { dot: projectManagementMetricDot(2), label: strings.AverageApprovalTime(), value: review.avgApprovalDays === undefined ? '—' : `${review.avgApprovalDays.toFixed(1)} ${strings.Days()}` },
        ]} />
        }
        <DividerTabs activeTab={effectiveView} onTabChange={(v) => selectView(v as ViewKey)} tabs={availableViews.map((v) => ({ id: v, label: viewLabel[v]() }))} />

        <div className="view-area">
          {
            !lifecycle ?
              <LoadingFlex />
              : effectiveView === 'table' ?
              // Fees follow the banner tile: a vertical without the RIC money model has none, and a
              // viewer who is not a fee viewer is not sent the parameters they would be summed from — so
              // the column would read $0 rather than nothing at all.
                  <ProjectManagementTableView rows={filteredRows} lifecycle={lifecycle} hideCash={!isAdmin} hideOwner={!isAdmin} nextTaskMode={!isAdmin} hideFees={financeModel !== 'ric' || !canSeePortfolioFees} />
                  : effectiveView === 'timeline' ?
                    // Gantt/Calendar toggle for everyone: admins plot milestones, members their own tasks.
                    <PipelineTimeline rows={filteredRows} lifecycle={lifecycle} taskMode={!isAdmin} />
                    : effectiveView === 'finances' ?
                      <ProjectManagementFinancesView rows={filteredRows} lifecycle={lifecycle} />
                      :
                      <ProjectManagementMapView rows={filteredRows} lifecycleKey={lifecycleKey} />
          }
        </div>
      </PageBody>
      {
        showCreate &&
          <Modal styled={WideModalStyled} title={strings.NewProject()} onClose={() => setShowCreate(false)}>
            {newProjectForm}
          </Modal>
      }
      {
        showDataRoom && lifecycle &&
          <DataRoomModal lifecycle={lifecycle} rows={filteredRows} onClose={() => setShowDataRoom(false)} />
      }
      {
        showBatchUpload && lifecycle &&
          <BatchUploadModal lifecycle={lifecycle} rows={rows} onClose={() => setShowBatchUpload(false)} />
      }
      {
        showNewPortfolio &&
          <Modal styled={WideModalStyled} title={strings.NewPortfolio()} onClose={() => setShowNewPortfolio(false)}>
            <div className="meta-row">{strings.Name()}</div>
            <Input value={newPortfolioName} onChange={(v) => setNewPortfolioName(v || '')} />
            <div className="modal-buttons">
              <Button className="secondary" onClick={() => setShowNewPortfolio(false)}>{strings.Cancel()}</Button>
              <Button className="primary" onClick={() => createPortfolio()}>{strings.Create()}</Button>
            </div>
          </Modal>
      }
    </ProjectManagementPipelinePageStyled>
  );
};
